// @ts-nocheck

import * as turf from '@turf/turf';
import { flatten, isEmpty, uniq, without } from 'lodash';
import moment from 'moment-timezone';
import { all, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { request as requester } from 'sonata-react-components';
import { isDigitalCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';

import {
  ANALYTICAL_VIEW_TIMEFRAME,
  formatDetailedStats,
  formatTimeFrame,
  getCartographyFilter,
  getTimeFrameForCustomTime,
  getVariableFilterParsed
} from '@/helpers/reporting.helpers';
import {
  getCartographyLevels,
  getInsightsData,
  getShareReports,
  getSummaryData
} from '@/modules/reporting/reporting.resources';
import {
  RELOAD_REPORTING_CARTOGRAPHY_FILTER_MAP,
  REPORTING_DOWNLOAD_INSIGHTS_DATA,
  REPORTING_DOWNLOAD_SUMMARY_DATA,
  REPORTING_FETCH_CAMPAIGN_SCREENS,
  REPORTING_FETCH_SHARED_REPORTS,
  REPORTING_MAP_CHANGE_MAP_VIEW,
  REPORTING_RESET_BEFORE_REFRESH,
  REPORTING_SET_COUNTRIES_WITH_EVENTS,
  reportingInsightsResetStats,
  reportingProgressResetStats,
  reportingResetMapData,
  reportingResetSectionsStats,
  reportingSetCampaignsScreens,
  reportingSetDefaultMapBoundingBox,
  reportingSetDownloadInsightsData,
  reportingSetDownloadSummaryData,
  reportingSetEventsByGeoH,
  reportingSetHeatmapData,
  reportingSetIndividualEvents,
  reportingSetSharedReports,
  reportingToggleMapLoading,
  SET_REPORTING_CARTOGRAPHY_FILTER_MAP
} from '@/store/actions/reporting.actions';
import {
  reportAdvancedFilterResetSection,
  reportingAdvancedFilterResetCartography
} from '@/store/actions/reportingAdvancedFilter.actions';
import { SET_MAP_BOUNDS, SET_MAP_TYPE } from '@/store/actions/reportingMap.actions';
import {
  getCampaignEvents,
  getCustomEvents,
  getDigitalConversionEvents,
  getMapLoading,
  getReportingType,
  getSelectedCampaigns,
  getStaticMode
} from '@/store/selectors/reporting.selectors';
import {
  getCampaigns,
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getCurrency,
  getTimeFrame,
  getTimeZone,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getAllBounds, getAllZooms, getMapType } from '@/store/selectors/reportingMap.selectors';

function getAdvanceFilterSection(id) {
  switch (id) {
    case 'reportingMap0':
    case 'reportingMap1':
      return 'maps';
    default:
      return 'general';
  }
}

function* buildCartographyFilter(section, mapId) {
  const cartographyMapId = section === 'maps' ? mapId : '';
  const generalCartographyCountries = yield select(state => getCartographyCountries(state, 'general'));
  const generalCartographyLevel = yield select(state => getCartographyLevel(state, 'general'));
  const generalCartographyLevelIds = yield select(state => getCartographyLevelIds(state, 'general'));
  const cartographyCountries = yield select(state => getCartographyCountries(state, section, cartographyMapId));
  const cartographyLevel = yield select(state => getCartographyLevel(state, section, cartographyMapId));
  const cartographyLevelIds = yield select(state => getCartographyLevelIds(state, section, cartographyMapId));

  return getCartographyFilter(
    {
      cartographyCountries: generalCartographyCountries,
      cartographyLevel: generalCartographyLevel,
      cartographyLevelIds: generalCartographyLevelIds
    },
    {
      cartographyCountries,
      cartographyLevel,
      cartographyLevelIds
    }
  );
}

function* buildTimeframe(section = 'general') {
  const timeFrame = yield select(state => getTimeFrame(state, section));
  const timeZone = yield select(state => getTimeZone(state, section));
  const momentFormat = 'YYYY-MM-DDTHH:mm:ss';
  const normalizedTimeFrame = {
    end: moment.utc(timeFrame.endDate).endOf('day').format(momentFormat),
    start: moment.utc(timeFrame.beginDate).startOf('day').format(momentFormat)
  };
  return formatTimeFrame(normalizedTimeFrame, timeZone);
}

function* getEventsBody(mapId, typeNames) {
  const section = getAdvanceFilterSection(mapId);
  const campaigns = yield select(getSelectedCampaigns);
  const campaignIds = yield select(getCampaigns);
  const allBounds = yield select(getAllBounds);
  const allZooms = yield select(getAllZooms);
  const timeZone = yield select(state => getTimeZone(state, 'general'));
  const timeFrame = yield select(state => getTimeFrame(state, 'general'));
  const cartography = yield buildCartographyFilter(section, mapId);
  const variableType = yield select(state => getVariableType(state, 'general'));
  const variableValues = yield select(state => getVariableValues(state, 'general'));
  const mapBounds = allBounds[mapId] || null;
  const mapZoom = allZooms[mapId] || null;
  const parsedTimeFrame = {
    end: timeFrame.endDate,
    start: timeFrame.beginDate
  };
  const userIds = uniq(campaigns.filter(c => campaignIds.includes(c.id)).map(campaign => campaign.user)).join();
  const typeName = typeNames ? typeNames.join(',') : undefined;

  const variableFilter =
    variableType && variableValues.length > 0 ? getVariableFilterParsed(variableType, variableValues) : undefined;
  const bodyParams = {
    ...parsedTimeFrame,
    campaignIds: campaignIds.join(),
    filters: {
      ...(!isEmpty(cartography) && { cartography }),
      ...variableFilter
    },
    timeZone,
    typeName,
    userIds
  };
  if (mapBounds) {
    Object.assign(bodyParams, {
      nwLat: mapBounds[1][1],
      nwLng: mapBounds[0][0],
      seLat: mapBounds[0][1],
      seLng: mapBounds[1][0],
      zoom: Math.ceil(mapZoom)
    });
  }
  return bodyParams;
}

function* getHeatmapData(mapId) {
  const selectedCampaigns = yield select(getSelectedCampaigns);
  if (!selectedCampaigns || !selectedCampaigns.length) {
    return;
  }
  yield delay(2000);
  const typeNames = yield select(getCampaignEvents);
  const body = yield getEventsBody(mapId, typeNames);
  const dataRaw = yield requester
    .makeRequest(`${taptap.config.backendUrlBase}report/heatMap`, {
      data: body,
      method: 'POST'
    })
    .catch(() => []);
  const features = dataRaw.map((data, id) => {
    return {
      ...turf.point([data.center[1], data.center[0]], data),
      id: `item_${id}`
    };
  });
  yield put(reportingSetHeatmapData(mapId, turf.featureCollection(features)));
}

function* setIndividualEvents(mapId) {
  const typeNamePromises = [];
  const selectedCampaigns = yield select(getSelectedCampaigns);
  if (!selectedCampaigns || !selectedCampaigns.length) {
    return;
  }
  let typeNames = yield select(getCampaignEvents);
  const digitalConvEvents = yield select(getDigitalConversionEvents);
  if (digitalConvEvents.length > 0) {
    typeNames.push(digitalConvEvents);
  }
  if (selectedCampaigns.some(campaign => campaign.hasDriveToStore)) {
    typeNames.push('fa_venue_uplift');
  }
  if (selectedCampaigns.some(campaign => campaign.hasNewAttributionStats && !campaign.hasDriveToStore)) {
    typeNames.push('footfall_attribution');
  }
  typeNames = without(flatten(typeNames), 'impressions');
  for (let i = 0; i < typeNames.length; i++) {
    const body = yield getEventsBody(mapId, [typeNames[i]]);
    typeNamePromises.push(
      requester
        .makeRequest(`${taptap.config.backendUrlBase}report/events`, {
          data: body,
          method: 'POST'
        })
        .catch(() => [])
    );
  }
  const individualEvents = yield Promise.all(typeNamePromises).then(results => {
    return results.map(eventsData => {
      const types = [];
      for (let i = 0; i < eventsData.length; i++) {
        if (types.indexOf(eventsData[i].typeName) === -1) {
          types.push(eventsData[i].typeName);
        }
      }
      return eventsData;
    });
  });
  const flattenEvents = flatten(individualEvents);
  const featureCollection = turf.featureCollection(
    flattenEvents.map((event, id) => {
      return {
        ...turf.point([event.longitude, event.latitude], { type: event.typeName }),
        id
      };
    })
  );
  yield put(reportingSetIndividualEvents(mapId, featureCollection));
}

function* setEventsByMapBounds(mapId) {
  const selectedCampaigns = yield select(getSelectedCampaigns);
  if (!selectedCampaigns || !selectedCampaigns.length) {
    return;
  }
  const reportingType = yield select(getReportingType);
  const typeNames = !isDigitalCampaign(reportingType) ? ['impressions', 'playouts'] : ['impressions'];
  const body = yield getEventsBody(mapId, typeNames);
  const eventsByGeoH = yield requester
    .makeRequest(`${taptap.config.backendUrlBase}report/reportsByGeo`, {
      data: body,
      method: 'POST'
    })
    .catch(() => []);
  const featureCollection = turf.featureCollection(
    eventsByGeoH.map((event, id) => {
      const line = turf.lineString([
        [event.bbox.southEast.longitude, event.bbox.southEast.latitude],
        [event.bbox.northWest.longitude, event.bbox.northWest.latitude]
      ]);
      const polygon = turf.bboxPolygon(turf.bbox(line));
      typeNames.forEach(typeName => {
        polygon.properties[typeName] = event[typeName];
      });
      polygon.id = id + 1;
      return polygon;
    })
  );
  yield put(reportingSetEventsByGeoH(mapId, featureCollection));
}

function* getMapData(action) {
  const { id: mapId } = action.data;
  const mapType = yield select(store => getMapType(store, mapId));
  if (!mapId.startsWith('reportingMap') && mapType !== 'cartography') {
    return;
  }
  const selectedCampaigns = yield select(getSelectedCampaigns);
  if (!selectedCampaigns || !selectedCampaigns.length) {
    return;
  }
  const isLoading = yield select(store => getMapLoading(store, mapId));
  if (isLoading) {
    return;
  }
  yield put(reportingToggleMapLoading(mapId, true));
  const staticMode = yield select(getStaticMode);
  const mapPromises = [setIndividualEvents(mapId)];
  if (!staticMode) {
    mapPromises.push(setEventsByMapBounds(mapId), getHeatmapData(mapId));
  }
  yield all(mapPromises);
  yield put(reportingToggleMapLoading(mapId, false));
}

function* changeMapView(action) {
  const { mapId, section } = action.data;
  const cartographyCountries = yield select(state => getCartographyCountries(state, section, mapId));
  const cartographyLevelIds = yield select(state => getCartographyLevelIds(state, section, mapId));

  if (cartographyCountries.length || cartographyLevelIds.length) {
    yield put(reportingResetMapData(mapId));
  }
  yield put(reportingAdvancedFilterResetCartography(section, mapId));
}

function* getDefaultMapBoundingBox(action) {
  const { countriesWithEvents } = action.data;

  const shapesBoundingBoxes = flatten(
    yield Promise.all(
      countriesWithEvents.map(countryCode =>
        getCartographyLevels('level0', countryCode, undefined, {}, ['boundingBox'])
      )
    )
  ).map(i => i.boundingBox);

  let minLeft = 180;
  let minBottom = 90;
  let maxRight = -180;
  let maxTop = -90;

  shapesBoundingBoxes.forEach(([left, bottom, right, top]) => {
    if (left < minLeft) minLeft = left;
    if (bottom < minBottom) minBottom = bottom;
    if (right > maxRight) maxRight = right;
    if (top > maxTop) maxTop = top;
  });

  yield put(reportingSetDefaultMapBoundingBox([minLeft, minBottom, maxRight, maxTop]));
}

function* getStatsBody(groups, timeStep, customTimeFrame, section, forReportingStats) {
  const timezone = yield select(state => getTimeZone(state, section));
  let timeFrame = customTimeFrame;
  if (!timeFrame) {
    timeFrame = yield buildTimeframe(section);
  }
  let campaignIds;
  let userIds;
  if (forReportingStats) {
    campaignIds = yield select(getCampaigns);
    const campaigns = yield select(getSelectedCampaigns);
    userIds = uniq(campaigns.filter(c => campaignIds.includes(c.id)).map(campaign => campaign.user));
  }
  const currency = yield select(state => getCurrency(state, section));
  const hasCustomEvents = yield select(getCustomEvents);
  const cartography = yield buildCartographyFilter(section);
  const variableType = yield select(state => getVariableType(state, section));
  const variableValues = yield select(state => getVariableValues(state, section));
  const variableFilter =
    variableType && variableValues.length > 0 ? getVariableFilterParsed(variableType, variableValues) : undefined;
  return {
    opts: {
      currencyOptions: {
        currency
      },
      includeCustomKpis: hasCustomEvents,
      includeUniques: true
    },
    params: {
      dateInterval: {
        timezone,
        ...timeFrame
      },
      groups,
      timeStep,
      ...(forReportingStats && {
        campaignIds: campaignIds.join(','),
        cartography,
        timezone,
        userIds: userIds.join(','),
        ...(timeFrame && timeFrame)
      }),
      filters: {
        ...(!isEmpty(cartography) && { cartography }),
        ...variableFilter
      }
    }
  };
}

function* fetchCampaignsScreens(action) {
  const { campaignIds } = action.data;
  const selectedCampaigns = yield select(getSelectedCampaigns);
  const screens = yield all(
    campaignIds.map(campaignId => {
      const campaign = selectedCampaigns.find(c => c.id === campaignId);
      const country = normalizeCountryToCca3(campaign?.countries[0]);
      const urlBase = `${taptap.config.backendUrlBase}screens/${campaignId}`;
      return requester.makeRequest(
        `${urlBase}/mata-hari-info?itemsPerPage=0&populate=true&includeDeleted=true&country=${country}`,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
      );
    })
  );
  const campaignsScreens = screens.reduce((acc, campaignScreens) => acc.concat(campaignScreens), []);
  yield put(reportingSetCampaignsScreens(campaignsScreens));
}

function* fetchSharedReports(action) {
  const { campaignIds, type } = action.data;
  const sharedReports = yield getShareReports(campaignIds, type);
  yield put(reportingSetSharedReports(sharedReports));
}

function* getFileSheetSummaryRows(fileFormat, section = 'summary', campaignsData = []) {
  if (fileFormat === 'excel') {
    const timeFrame = yield select(store => getTimeFrame(store, section));
    const momentFormat = 'YYYY-MM-DD HH:mm:ss';
    const { beginDate, endDate } = timeFrame;
    return [
      ...(campaignsData.length ? [campaignsData.map(campaign => `${campaign.name} (${campaign.id})`).join(' / ')] : []),
      `Report start date: ${moment.utc(beginDate).startOf('day').format(momentFormat)}`,
      `Report end date: ${moment.utc(endDate).endOf('day').format(momentFormat)}`,
      `Export date: ${moment().format(momentFormat)}`
    ];
  }
  return [];
}

function* buildBodyFromSection(section = 'general') {
  const campaignIds = yield select(getCampaigns);
  const currency = yield select(store => getCurrency(store, section));
  const cartography = yield buildCartographyFilter(section);
  const timezone = yield select(store => getTimeZone(store, section));
  const timeFrame = yield buildTimeframe(section);
  const variableType = yield select(store => getVariableType(store, section));
  const variableValues = yield select(store => getVariableValues(store, section));
  const variableFilter =
    variableType && variableValues.length > 0 ? getVariableFilterParsed(variableType, variableValues) : undefined;

  return {
    campaignIds,
    opts: {
      currencyOptions: {
        currency
      },
      includeUniques: true
    },
    params: {
      dateInterval: {
        timezone,
        ...timeFrame
      },
      filters: {
        ...(!isEmpty(cartography) && { cartography }),
        ...variableFilter
      }
    }
  };
}

function* downloadSummaryData(action) {
  const { fileColumns, fileFormat, sheetHeadersTitle, token } = action.data;
  const campaigns = yield select(getSelectedCampaigns);
  const campaignIds = yield select(getCampaigns);
  const sheetSummaryRows = yield getFileSheetSummaryRows(fileFormat);
  const initialBody = yield buildBodyFromSection('summary');
  let requestFinishedWithError = false;
  try {
    const userId = uniq(campaigns.filter(c => campaignIds.includes(c.id)).map(campaign => campaign.user))[0];
    yield put(reportingSetDownloadSummaryData('processing', fileFormat, null));
    const summaryDataBody = {
      ...initialBody,
      opts: {
        ...initialBody.opts,
        excelExtraRows: {
          sheetHeadersTitle,
          sheetSummaryRows
        },
        fileColumns,
        includeUniques: true
      },
      params: {
        ...initialBody.params,
        groups: ['cid']
      }
    };
    const summaryData = yield getSummaryData(userId, campaignIds, summaryDataBody, fileFormat, token).catch(() => {
      requestFinishedWithError = true;
    });
    yield put(reportingSetDownloadSummaryData('finished', fileFormat, summaryData));
  } catch (err) {
    requestFinishedWithError = true;
  }
  if (requestFinishedWithError) {
    return yield put(reportingSetDownloadSummaryData('finished', fileFormat, null));
  }
}

function* downloadInsightsData(action) {
  const { insights, params, section = 'general', token } = action.data;
  const campaigns = yield select(getSelectedCampaigns);
  const campaignIds = yield select(getCampaigns);
  const sheetSummaryRows = yield getFileSheetSummaryRows('excel', section, campaigns);
  const initialBody = yield buildBodyFromSection(section);
  let requestFinishedWithError = false;
  try {
    const userId = uniq(campaigns.filter(c => campaignIds.includes(c.id)).map(campaign => campaign.user))[0];
    yield put(reportingSetDownloadInsightsData('processing', null));
    const insightsDataBody = {
      campaignIds,
      insights,
      opts: {
        currencyOptions: {
          ...initialBody.opts.currencyOptions
        },
        includeUniques: true
      },
      params: {
        ...params,
        dateInterval: {
          ...initialBody.params.dateInterval
        },
        filters: {
          ...params.filters,
          ...initialBody.params.filters
        },
        sheetSummaryRows
      }
    };
    const insightsData = yield getInsightsData(userId, insightsDataBody, token).catch(() => {
      requestFinishedWithError = true;
    });
    yield put(reportingSetDownloadInsightsData('finished', insightsData));
  } catch (err) {
    requestFinishedWithError = true;
  }
  if (requestFinishedWithError) {
    return yield put(reportingSetDownloadInsightsData('finished', null));
  }
}

function* resetBeforeRefresh() {
  yield all([
    put(reportingResetSectionsStats()),
    put(reportingInsightsResetStats()),
    put(reportingProgressResetStats()),
    put(reportAdvancedFilterResetSection('summary'))
  ]);
}

export function* sagas() {
  yield all([
    takeLatest([REPORTING_FETCH_CAMPAIGN_SCREENS], fetchCampaignsScreens),
    takeLatest([REPORTING_FETCH_SHARED_REPORTS], fetchSharedReports),
    takeLatest([SET_REPORTING_CARTOGRAPHY_FILTER_MAP], getMapData),
    takeLatest([REPORTING_MAP_CHANGE_MAP_VIEW], changeMapView),
    takeLatest([RELOAD_REPORTING_CARTOGRAPHY_FILTER_MAP], getMapData),
    takeEvery([SET_MAP_BOUNDS, SET_MAP_TYPE], getMapData),
    takeEvery([REPORTING_DOWNLOAD_SUMMARY_DATA], downloadSummaryData),
    takeEvery([REPORTING_DOWNLOAD_INSIGHTS_DATA], downloadInsightsData),
    takeEvery([REPORTING_RESET_BEFORE_REFRESH], resetBeforeRefresh),
    takeEvery([REPORTING_SET_COUNTRIES_WITH_EVENTS], getDefaultMapBoundingBox)
  ]);
}
